import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, chakra, Flex, Grid, Heading, Image, Text } from '@chakra-ui/react';
import type { MediaItem } from '@denise-augusta/domain';
import { executeAction } from '@/lattice/runtime';
import { usePortalUser } from '@/auth/roles';

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function MediaLibrary() {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const user = usePortalUser();

  const refresh = useCallback(() => {
    executeAction<MediaItem[]>('media-library:listMedia', {}, user)
      .then(setItems)
      .catch((err: Error) => setError(err.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        await executeAction(
          'media-library:uploadMedia',
          {
            name: file.name,
            contentType: file.type || 'application/octet-stream',
            dataBase64: await toBase64(file),
          },
          user
        );
      }
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const remove = async (name: string) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    setBusy(true);
    try {
      await executeAction('media-library:deleteMedia', { name }, user);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb="1.2rem" wrap="wrap" gap="1rem">
        <Box>
          <Heading fontFamily="heading" fontSize="2.2rem" fontWeight="300" color="brand.ink">
            Media Library
          </Heading>
          <Text fontSize="0.85rem" color="ink.soft">
            Images and files in the <code>media</code> blob container.
          </Text>
        </Box>
        <chakra.button
          onClick={() => fileInput.current?.click()}
          disabled={busy}
          bg="brand.mid"
          color="paper.white"
          fontSize="0.72rem"
          fontWeight="500"
          letterSpacing="0.15em"
          textTransform="uppercase"
          px="2.2rem"
          py="0.85rem"
          border="none"
          cursor="pointer"
          borderRadius="24px"
          opacity={busy ? 0.6 : 1}
          _hover={{ bg: 'brand.ink' }}
        >
          {busy ? 'Working…' : 'Upload Files'}
        </chakra.button>
        <input
          ref={fileInput}
          type="file"
          multiple
          accept="image/*,.pdf"
          style={{ display: 'none' }}
          onChange={(event) => void upload(event.target.files)}
        />
      </Flex>

      {error && (
        <Box bg="#FBEDEB" border="1px solid" borderColor="brand.terracotta" borderRadius="10px" px="1.2rem" py="0.8rem" mb="1rem">
          <Text fontSize="0.85rem" color="brand.terracotta">
            {error}
          </Text>
        </Box>
      )}

      {items.length === 0 ? (
        <Box bg="paper.white" border="1px dashed" borderColor="paper.border" borderRadius="16px" p="3rem" textAlign="center">
          <Text fontSize="0.9rem" color="ink.soft">
            No media yet — upload the site images to get started.
          </Text>
        </Box>
      ) : (
        <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap="1.2rem">
          {items.map((item) => (
            <Box key={item.name} bg="paper.white" border="1px solid" borderColor="paper.border" borderRadius="12px" overflow="hidden">
              {item.contentType?.startsWith('image/') ? (
                <Image src={item.url} alt={item.name} w="100%" h="140px" objectFit="cover" />
              ) : (
                <Flex h="140px" align="center" justify="center" bg="brand.wash" color="brand.mid" fontSize="2rem">
                  📄
                </Flex>
              )}
              <Box p="0.8rem">
                <Text fontSize="0.75rem" fontWeight="500" color="ink.body" truncate title={item.name}>
                  {item.name}
                </Text>
                <Flex justify="space-between" align="center" mt="0.4rem">
                  <Text fontSize="0.68rem" color="ink.muted">
                    {item.sizeBytes ? `${Math.round(item.sizeBytes / 1024)} KB` : ''}
                  </Text>
                  <Box
                    as="button"
                    onClick={() => void remove(item.name)}
                    fontSize="0.68rem"
                    letterSpacing="0.08em"
                    textTransform="uppercase"
                    color="brand.terracotta"
                    bg="transparent"
                    border="none"
                    cursor="pointer"
                  >
                    Delete
                  </Box>
                </Flex>
              </Box>
            </Box>
          ))}
        </Grid>
      )}
    </Box>
  );
}
